import {
  Building2,
  ChevronDown,
  ChevronRight,
  LogOut,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { cn } from '../lib/cn';

/** A leaf entry inside an expandable group. */
export interface SidebarChildItem {
  id: string;
  label: string;
  href: string;
  /** When `true`, the entry is omitted from the rendered tree (RBAC). */
  hidden?: boolean;
}

/** Top-level menu entry — either a direct link or an expandable group. */
export interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Direct link target. Mutually exclusive with `children`. */
  href?: string;
  /** Sub-entries — when present, the item renders as an expandable group. */
  children?: SidebarChildItem[];
  /**
   * Path prefix(es) that mark this item as active. Defaults to `href` for
   * leaf items, or the union of children hrefs for groups.
   */
  matchPrefix?: string | string[];
  /** Treat the literal `/` route as if it matched this item. */
  matchRoot?: boolean;
  /** Initial open state for groups. Defaults to "open if currently active". */
  defaultOpen?: boolean;
  /** Hide this item entirely (e.g., RBAC). */
  hidden?: boolean;
}

export interface SidebarUser {
  displayName: string;
  role?: string;
}

export interface SidebarBrand {
  title: string;
  /** Lucide icon to render in the brand square. Defaults to Building2. */
  icon?: LucideIcon;
}

export interface SidebarProps {
  /** Mobile drawer open state. Ignored on lg+ where the sidebar is static. */
  isOpen: boolean;
  onToggle: () => void;

  /** Brand block at the top-left. */
  brand: SidebarBrand;
  /** Optional slot under the brand title (e.g., TenantSwitcher). */
  headerSlot?: React.ReactNode;

  /** Top-level menu structure. */
  items: SidebarItem[];

  /** User block at the bottom. */
  user: SidebarUser;
  onSignOut: () => void;
}

function asArray(v: string | string[] | undefined): string[] {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
}

function isItemActive(pathname: string, item: SidebarItem): boolean {
  const prefixes = asArray(item.matchPrefix);
  if (prefixes.length === 0) {
    if (item.href) prefixes.push(item.href);
    if (item.children) prefixes.push(...item.children.map((c) => c.href));
  }
  if (item.matchRoot && pathname === '/') return true;
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function isChildActive(pathname: string, child: SidebarChildItem): boolean {
  return pathname === child.href || pathname.startsWith(`${child.href}/`);
}

/**
 * Shared shell sidebar. Keeps the Old TMS look-and-feel (slate-800 chrome,
 * blue-600 active state, lucide icons) but is fully data-driven so each app
 * (Tms / Depot / Expenses) supplies its own menu, brand, and user info.
 *
 * Notes vs. My.Tms.Old:
 *   - No Auth0 / MobX / tenant coupling — caller passes `user` + `onSignOut`
 *     and slots in a `headerSlot` (TenantSwitcher, etc.).
 *   - `react-router` (v7) instead of `react-router-dom`.
 *   - RBAC is handled by the caller via the `hidden` flag on items/children.
 */
export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  brand,
  headerSlot,
  items,
  user,
  onSignOut,
}) => {
  const location = useLocation();
  const BrandIcon = brand.icon ?? Building2;

  return (
    <div
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-slate-800 text-white transition-transform duration-300 ease-in-out',
        isOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:static lg:inset-0 lg:translate-x-0',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-700 px-3 py-1.5">
        <div className="flex items-center space-x-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-600">
            <BrandIcon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{brand.title}</h1>
            {headerSlot}
          </div>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="rounded-md p-2 hover:bg-slate-700 lg:hidden"
          aria-label="Close sidebar"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2 px-2 py-2">
        {items
          .filter((item) => !item.hidden)
          .map((item) =>
            item.children ? (
              <SidebarGroup key={item.id} item={item} pathname={location.pathname} />
            ) : (
              <SidebarLeaf key={item.id} item={item} pathname={location.pathname} />
            ),
          )}
      </nav>

      {/* User section */}
      <div className="mt-auto border-t border-slate-700 p-4">
        <div className="mb-4 flex items-center space-x-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600">
            <UserIcon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{user.displayName}</p>
            {user.role && <p className="text-sm text-slate-400">{user.role}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={onSignOut}
          className="flex w-full items-center space-x-3 rounded-lg px-3 py-2 text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
        >
          <LogOut className="h-5 w-5" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

interface SidebarLeafProps {
  item: SidebarItem;
  pathname: string;
}

const SidebarLeaf: React.FC<SidebarLeafProps> = ({ item, pathname }) => {
  if (!item.href) return null;
  const Icon = item.icon;
  const active = isItemActive(pathname, item);
  return (
    <Link
      to={item.href}
      className={cn(
        'flex items-center space-x-3 rounded-lg px-2 py-1 transition-colors',
        active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white',
      )}
    >
      <Icon className="h-5 w-5" />
      <span className="font-medium">{item.label}</span>
    </Link>
  );
};

interface SidebarGroupProps {
  item: SidebarItem;
  pathname: string;
}

const SidebarGroup: React.FC<SidebarGroupProps> = ({ item, pathname }) => {
  const active = isItemActive(pathname, item);
  const Icon = item.icon;
  const [open, setOpen] = useState<boolean>(item.defaultOpen ?? active);
  const Chevron = open ? ChevronDown : ChevronRight;
  const visibleChildren = (item.children ?? []).filter((c) => !c.hidden);
  if (visibleChildren.length === 0) return null;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2',
          active ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white',
        )}
      >
        <span className="flex items-center space-x-3">
          <Icon className="h-5 w-5" />
          <span className="font-medium">{item.label}</span>
        </span>
        <Chevron className="h-4 w-4" />
      </button>
      {open && (
        <div className="ml-8 space-y-1">
          {visibleChildren.map((child) => (
            <Link
              key={child.id}
              to={child.href}
              className={cn(
                'block rounded-md px-3 py-1 text-sm',
                isChildActive(pathname, child)
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white',
              )}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
